/**
 * Helpers for turning raw storefront sale-product data into the strings a
 * member actually reads.
 *
 * Mirrors maui's customize summary (`getColorData` / `isColorDesciptor` in
 * `packages/apps/maui/app/_utils/products`) so the same selection reads the
 * same on both surfaces. Kept as a local copy because those helpers are
 * app-private to maui and plutus cannot import across apps.
 */

const isColorDescriptor = (descriptor) => descriptor?.type?.toUpperCase() === 'COLOR';

/**
 * Strips the swatch payload off an encoded COLOR value.
 *
 * Storefront encodes COLOR descriptors as `"<Display Name>@<hex>"`, or
 * `"<Display Name>@<hex>|<hex>"` for two-tone swatches, so only the segment
 * before the `@` is human readable.
 * @param {unknown} value Raw descriptor value, e.g. `'Rose Gold@#B76E79'`.
 * @returns {string} The readable segment, or an empty string for a non-string value.
 */
const getColorDisplayName = (value) => {
  if (typeof value !== 'string') return '';
  const separatorIndex = value.indexOf('@');
  return separatorIndex === -1 ? value : value.slice(0, separatorIndex);
};

/**
 * Builds the human-readable option label for a single sale-product variant.
 *
 * Non-COLOR descriptors (`WORD`, `WORD1`, …) carry their display text directly
 * in `value`.
 * @param {{ variantDescriptors?: { name?: string, type?: string, value?: string }[] }} [variant]
 *   The variant the member selected, as returned by the storefront sale-products API.
 * @returns {string|null} e.g. `'Rose Gold'` or `'Rose Gold, Large'`, or `null`
 *   when the variant has nothing worth showing.
 */
export const getVariantLabel = (variant) => {
  const parts = (variant?.variantDescriptors ?? [])
    .map((descriptor) =>
      isColorDescriptor(descriptor) ? getColorDisplayName(descriptor?.value) : descriptor?.value,
    )
    .filter((part) => typeof part === 'string' && part.trim() !== '')
    .map((part) => part.trim());

  return parts.length > 0 ? parts.join(', ') : null;
};
